import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Row, Col, Dropdown } from 'react-bootstrap';
import { useCompanyStore, useBreadcrumbStore } from 'stores';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
//api
import { getAllListsNotPaginated } from 'api/lists';
//custom-hook
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
import useLocalePath from 'utils/customHooks/useLocalePath';
//images
import menuBooks from 'assets/img/img_menu_books.png';

const CustomDropdown = ({ setShowOffCanvas, isDesktop, menu }) => {
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);
  const [allLists, setAllLists] = useState([]);
  const MenuToggler = useRef(undefined);
  const listsLoadedOkRef = useRef(false);
  const { matters } = useCompanyStore();
  const { setBreadcrumbTitle } = useBreadcrumbStore();
  const { t, i18n } = useTranslation(['header']);
  const { libraryPath } = useAssignedLibrary();
  const localePath = useLocalePath();
  const currentLanguage = i18n.language;

  const desktopText = t(menu[`text_${currentLanguage}`]?.toLocaleUpperCase() || menu.text.toLocaleUpperCase());
  const mobileText = `${t(menu[`text_${currentLanguage}`]?.toLocaleUpperCase() || menu.text.toLocaleUpperCase())} / (${t('materias')})`;

  // getAllLists() sin params saturaba el API; usamos all-lists-not-paginated (liviano) al abrir el menú.
  useEffect(() => {
    if (!showMenu || listsLoadedOkRef.current) return;

    let cancelled = false;

    const fetchLists = async () => {
      try {
        const { status, data } = await getAllListsNotPaginated();
        if (cancelled || status !== 200 || data == null) return;
        const rows = Array.isArray(data) ? data : data?.results;
        if (Array.isArray(rows)) {
          setAllLists(rows);
          listsLoadedOkRef.current = true;
        }
      } catch (error) {
        console.error('Error al obtener listas:', error);
      }
    };

    fetchLists();
    return () => {
      cancelled = true;
    };
  }, [showMenu]);

  const closeMenu = () => {
    if (MenuToggler.current) MenuToggler.current.click();
    if (setShowOffCanvas) setShowOffCanvas(false);
    setShowMenu(false);
  };

  const handleClick = (title) => {
    setBreadcrumbTitle(title);
  };

  const foundNameList = (nameList) => {
    if (!allLists || !Array.isArray(allLists)) return null;

    const found = allLists.find((list) => list.nombre === nameList);

    return found;
  };

  // Ordenar y dividir los elementos en tres columnas
  const sortedMatters = useMemo(() => {
    if (!matters || !Array.isArray(matters)) return [];
    return [...matters].sort((a, b) => {
      const nameA = a[`name_${i18n.language}`] || a.name || '';
      const nameB = b[`name_${i18n.language}`] || b.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [matters, i18n.language]);

  const columns = useMemo(() => {
    const numColumns = 3;
    const columnSize = Math.ceil(sortedMatters.length / numColumns);
    return Array.from({ length: numColumns }, (_, colIndex) =>
      sortedMatters.slice(colIndex * columnSize, (colIndex + 1) * columnSize),
    );
  }, [sortedMatters]);

  // Función para renderizar el menú de submenu
  const renderSubmenu = () => {
    return menu?.submenu?.map((item) => {
      if (!item) return null;
      const { text, url, id, isExternal } = item;
      let finalUrl = '';
      if (typeof url === 'string') {
        finalUrl = url.trim();
        if (finalUrl.startsWith('/libelista')) {
          finalUrl = finalUrl.replace('/libelista', '');
        }
      } else if (typeof url === 'object' && url !== null) {
        finalUrl = `${url.pathname}${url.search || ''}`;
        if (finalUrl.startsWith('/libelista')) {
          finalUrl = finalUrl.replace('/libelista', '');
        }
      }

      const localizedText = item[`text_${i18n.language}`] || text;
      const processedText = text === 'Las librerías recomiendan' ? 'Recomiendan librerias' : text;

      const nameList = foundNameList(processedText);
      const fullUrl = finalUrl === '/' ? localePath('/') : libraryPath(finalUrl.replace(/^\//, ''));

      // Si es una URL externa, usar href en lugar de Link
      if (isExternal && finalUrl) {
        return (
          <Dropdown.Item
            className="text-uppercase customDropdown-item"
            href={finalUrl}
            key={id}
            onClick={() => {
              handleClick(text);
              closeMenu();
            }}
          >
            {localizedText}
          </Dropdown.Item>
        );
      }

      return (
        <Dropdown.Item
          className="text-uppercase customDropdown-item"
          as={Link}
          to={fullUrl}
          key={id}
          state={{
            list: (nameList?.id || id).toString(),
            ...(location.state || {}),
          }}
          eventKey={url}
          onClick={() => {
            handleClick(text);
            closeMenu();
          }}
        >
          {localizedText}
        </Dropdown.Item>
      );
    });
  };

  return (
    <Dropdown className="customDrop" show={showMenu} onToggle={(isOpen) => setShowMenu(isOpen)}>
      <Dropdown.Toggle ref={MenuToggler}>{isDesktop ? desktopText : mobileText}</Dropdown.Toggle>
      <Dropdown.Menu rootCloseEvent="click">
        <Row>
          <Col sm={12} md={12} lg={4} xl={4} className="d-flex flex-column justify-content-center align-items-center">
            <Dropdown.Item disabled>
              <img className="mb-2 mt-n5" src={menuBooks} alt="books.png" />
            </Dropdown.Item>
            {renderSubmenu()}
          </Col>
          <Col sm={12} md={12} lg={6} xl={8}>
            <p className="text-uppercase bg-light p-2 mb-2">{t('materias')}</p>
            <div className="ml-3 d-flex flex-column flex-lg-wrap custom-accordion">
              <Row className="g-3">
                {columns.map((column, colIndex) => (
                  <Col xs={12} sm={12} md={12} lg={4} key={`column-${colIndex}`}>
                    {column.map((matt, index) => {
                      const translatedName = matt[`name_${i18n.language}`] || matt.name;
                      const formattedTitle = matt.name.replace(/\s+/g, '');
                      const matterIbicId = matt.padre_id || matt.id;
                      return (
                        <Link
                          to={libraryPath(`lista/materias?book_ibic=${matterIbicId}&title=${formattedTitle}`)}
                          key={matt.name + '-' + index}
                          className="d-block m-0 px-0 w-auto hover-text border-bottom-item"
                          onClick={closeMenu}
                          title={matt.name}
                        >
                          {translatedName.toLocaleUpperCase()}
                        </Link>
                      );
                    })}
                  </Col>
                ))}
              </Row>
            </div>
          </Col>
        </Row>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default CustomDropdown;
